import { z } from 'zod';
import { callWindowSchema, communicationChannelSchema } from '../common/types';

// =============================================================================
// Channel Types
// =============================================================================

export const agentChannelSchema = z.enum(['phone', 'sms', 'email', 'push']);
export type AgentChannel = z.infer<typeof agentChannelSchema>;

// =============================================================================
// LLM Configuration
// =============================================================================

export const llmConfigSchema = z.object({
  provider: z.enum(['openai', 'anthropic', 'groq', 'together']),
  model: z.string().min(1),
  temperature: z.number().min(0).max(2).optional(),
  maxTokens: z.number().int().positive().optional(),
  fallbacks: z
    .array(
      z.object({
        order: z.number().int().positive(),
        provider: z.string(),
        model: z.string(),
      })
    )
    .optional(),
});

export type LLMConfig = z.infer<typeof llmConfigSchema>;

// =============================================================================
// Voice Configuration
// =============================================================================

export const voiceConfigSchema = z.object({
  provider: z.enum(['elevenlabs', 'playht', 'cartesia', 'deepgram', 'openai']),
  voiceId: z.string().min(1),
  model: z.string().optional(),
  speed: z.number().min(0.5).max(2).optional(),
  stability: z.number().min(0).max(1).optional(),
  similarityBoost: z.number().min(0).max(1).optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
  fallbacks: z
    .array(
      z.object({
        order: z.number().int().positive(),
        provider: z.string(),
        voiceId: z.string(),
      })
    )
    .optional(),
});

export type VoiceConfig = z.infer<typeof voiceConfigSchema>;

// =============================================================================
// Transcriber / Call / Voicemail / SMS Configuration
// =============================================================================

export const transcriberConfigSchema = z.object({
  provider: z.enum(['deepgram', 'openai', 'assembly']),
  model: z.string().min(1),
  language: z.string().optional(),
  fallbacks: z
    .array(
      z.object({
        order: z.number().int().positive(),
        provider: z.string(),
        model: z.string(),
      })
    )
    .optional(),
});

export type TranscriberConfig = z.infer<typeof transcriberConfigSchema>;

export const callConfigSchema = z.object({
  maxDurationSeconds: z.number().int().positive(),
  silenceTimeoutSeconds: z.number().int().positive(),
  firstMessage: z.string().optional(),
  waitForUserFirst: z.boolean().optional(),
  endCallPhrases: z.array(z.string()).optional(),
});

export type CallConfig = z.infer<typeof callConfigSchema>;

export const voicemailConfigSchema = z.object({
  detectionEnabled: z.boolean(),
  action: z.enum(['leave_message', 'hangup_retry', 'hangup_escalate']),
  messageTemplate: z.string().optional(),
});

export type VoicemailConfig = z.infer<typeof voicemailConfigSchema>;

export const smsConfigSchema = z.object({
  firstMessageTemplate: z.string().min(1),
  maxMessages: z.number().int().positive(),
  replyTimeoutMinutes: z.number().int().positive(),
  conversationTimeoutHours: z.number().int().positive(),
  maxMessageLength: z.number().int().positive(),
  noReplyAction: z.enum(['retry', 'next_rule', 'escalate']),
  maxNoReplyRetries: z.number().int().min(0),
  includeOptOut: z.boolean(),
  optOutText: z.string().optional(),
});

export type SmsConfig = z.infer<typeof smsConfigSchema>;

// =============================================================================
// Structured Output Field
// =============================================================================

export const structuredOutputFieldSchema = z.object({
  name: z.string().min(1),
  type: z.enum(['string', 'number', 'boolean', 'string[]', 'date']),
  description: z.string(),
  required: z.boolean(),
  enumValues: z.array(z.string()).optional(),
});

export type StructuredOutputField = z.infer<typeof structuredOutputFieldSchema>;

// =============================================================================
// Outcome Category
// =============================================================================

export const outcomeCategorySchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  isTerminal: z.boolean().optional(),
  requiresEscalation: z.boolean().optional(),
});

export type OutcomeCategory = z.infer<typeof outcomeCategorySchema>;

// =============================================================================
// Outbound Configuration
// =============================================================================

export const outboundConfigSchema = z.object({
  // Retry settings
  maxAttempts: z.number().int().min(1).max(10),
  retryDelayMinutes: z.number().int().min(1),
  retryOnOutcomes: z.array(z.string()), // e.g., ['no_answer', 'voicemail', 'busy']
  escalateOnOutcomes: z.array(z.string()), // e.g., ['needs_human', 'angry']

  // Default call windows (used when patient has no preferences)
  defaultCallWindows: z.array(callWindowSchema),

  // Default channel preference (ordered - first is highest priority)
  defaultChannels: z.array(communicationChannelSchema),
});

export type OutboundConfig = z.infer<typeof outboundConfigSchema>;

// =============================================================================
// Agent Summary Response
// =============================================================================

export const agentSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  avatarUrl: z.string().optional(),
  channel: agentChannelSchema,
  isActive: z.boolean(),
  baseConfigName: z.string().optional(),
  createdAt: z.string(),
});

export type AgentSummaryResponse = z.infer<typeof agentSummarySchema>;

// =============================================================================
// Resolved Agent Config Response
// =============================================================================

export const resolvedAgentConfigSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  avatarUrl: z.string().optional(),
  channel: agentChannelSchema,
  isActive: z.boolean(),
  systemPrompt: z.string(),
  llmConfig: llmConfigSchema,
  structuredOutputs: z.array(structuredOutputFieldSchema),
  outcomeCategories: z.array(outcomeCategorySchema),
  voiceConfig: voiceConfigSchema.optional(),
  transcriberConfig: z
    .object({
      provider: z.string(),
      model: z.string(),
      language: z.string().optional(),
    })
    .optional(),
  callConfig: z
    .object({
      maxDurationSeconds: z.number(),
      silenceTimeoutSeconds: z.number(),
      firstMessage: z.string().optional(),
    })
    .optional(),
  voicemailConfig: z
    .object({
      detectionEnabled: z.boolean(),
      action: z.enum(['leave_message', 'hangup_retry', 'hangup_escalate']),
      messageTemplate: z.string().optional(),
    })
    .optional(),
  smsConfig: z
    .object({
      firstMessageTemplate: z.string(),
      maxMessages: z.number(),
      replyTimeoutMinutes: z.number(),
    })
    .optional(),
});

export type ResolvedAgentConfigResponse = z.infer<typeof resolvedAgentConfigSchema>;

// =============================================================================
// Base Config Summary
// =============================================================================

export const baseConfigSummarySchema = z.object({
  name: z.string(),
  displayName: z.string(),
  description: z.string().optional(),
  channel: agentChannelSchema,
});

export type BaseConfigSummaryResponse = z.infer<typeof baseConfigSummarySchema>;

// =============================================================================
// Create/Update Requests
// =============================================================================

export const createAgentRequestSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  avatarUrl: z.string().url().optional(),
  channel: agentChannelSchema,
  isActive: z.boolean().optional(),
  baseConfigName: z.string().optional(),

  // Overrides (used with baseConfigName)
  systemPromptOverride: z.string().optional(),
  llmConfigOverride: llmConfigSchema.partial().optional(),
  voiceConfigOverride: voiceConfigSchema.partial().optional(),
  transcriberConfigOverride: transcriberConfigSchema.partial().optional(),
  callConfigOverride: callConfigSchema.partial().optional(),
  voicemailConfigOverride: voicemailConfigSchema.partial().optional(),
  smsConfigOverride: smsConfigSchema.partial().optional(),
  structuredOutputsOverride: z.array(structuredOutputFieldSchema).optional(),
  outcomeCategoriesOverride: z.array(outcomeCategorySchema).optional(),

  // Standalone config (used without baseConfigName)
  systemPrompt: z.string().optional(),
  llmConfig: llmConfigSchema.optional(),
  voiceConfig: voiceConfigSchema.optional(),
  transcriberConfig: transcriberConfigSchema.optional(),
  callConfig: callConfigSchema.optional(),
  voicemailConfig: voicemailConfigSchema.optional(),
  smsConfig: smsConfigSchema.optional(),
  structuredOutputs: z.array(structuredOutputFieldSchema).optional(),
  outcomeCategories: z.array(outcomeCategorySchema).optional(),
});

export type CreateAgentRequest = z.infer<typeof createAgentRequestSchema>;

export const updateAgentRequestSchema = createAgentRequestSchema.partial();
export type UpdateAgentRequest = z.infer<typeof updateAgentRequestSchema>;

export const duplicateAgentRequestSchema = z.object({
  newName: z.string().min(1, 'New name is required'),
});

export type DuplicateAgentRequest = z.infer<typeof duplicateAgentRequestSchema>;

// =============================================================================
// Search Request
// =============================================================================

export const agentSearchRequestSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
  searchTerm: z.string().optional(),
  channel: agentChannelSchema.optional(),
  isActive: z.boolean().optional(),
});

export type AgentSearchRequest = z.infer<typeof agentSearchRequestSchema>;
